import React, { useMemo } from 'react';
import './ConfidenceTrendChart.css';

// Helper functions moved outside component
const calculateSlope = (values) => {
  if (values.length < 2) return 0;
  const n = values.length;
  const sumX = (n * (n - 1)) / 2;
  const sumY = values.reduce((a, b) => a + b, 0);
  const sumXY = values.reduce((sum, y, x) => sum + x * y, 0);
  const sumX2 = (n * (n - 1) * (2 * n - 1)) / 6;
  
  return (n * sumXY - sumX * sumY) / (n * sumX2 - sumX * sumX);
};

const calculateSentiment = (point) => {
  if (!point) return 'neutral';
  const score = (point.highPercentage * 1) + (point.medPercentage * 0) + (point.lowPercentage * -1);
  if (score > 20) return 'bullish';
  if (score < -20) return 'bearish';
  return 'neutral';
};

const calculateMomentum = (data) => {
  if (data.length < 2) return 'stable';
  const recent = data.slice(-3);
  const older = data.slice(-6, -3);
  
  const recentAvg = recent.reduce((sum, p) => sum + p.highPercentage, 0) / recent.length;
  const olderAvg = older.length > 0 ? 
    older.reduce((sum, p) => sum + p.highPercentage, 0) / older.length : recentAvg;
  
  const change = recentAvg - olderAvg;
  if (change > 5) return 'increasing';
  if (change < -5) return 'decreasing';
  return 'stable';
};

const ConfidenceTrendChart = ({ timeSeriesData }) => {
  // This chart shows the distribution of prediction confidence levels over time
  // HIGH confidence = users are very certain about their picks
  // MED confidence = moderate certainty
  // LOW confidence = users are uncertain/guessing
  // The chart helps identify market sentiment trends

  const { chartData, extrapolatedData, stats } = useMemo(() => {
    if (!timeSeriesData || timeSeriesData.length === 0) {
      return { chartData: [], extrapolatedData: [], stats: {} };
    }

    // Process time series data
    const processed = timeSeriesData.map(point => ({
      ...point,
      highPercentage: point.total > 0 ? (point.HIGH / point.total) * 100 : 0,
      medPercentage: point.total > 0 ? (point.MED / point.total) * 100 : 0,
      lowPercentage: point.total > 0 ? (point.LOW / point.total) * 100 : 0,
    }));

    // Simple linear extrapolation for next 24 hours
    const extrapolated = [];
    if (processed.length >= 2) {
      const recent = processed.slice(-6); // Last 6 data points for trend
      
      // Calculate trend slopes
      const highSlope = calculateSlope(recent.map(p => p.highPercentage));
      const medSlope = calculateSlope(recent.map(p => p.medPercentage));
      const lowSlope = calculateSlope(recent.map(p => p.lowPercentage));
      
      // Project forward
      const lastPoint = processed[processed.length - 1];
      const lastTime = new Date(lastPoint.timestamp);
      
      for (let i = 1; i <= 6; i++) {
        const futureTime = new Date(lastTime);
        futureTime.setHours(futureTime.getHours() + (i * 4)); // Project 4 hours ahead each point
        
        extrapolated.push({
          timestamp: futureTime,
          highPercentage: Math.max(0, Math.min(100, lastPoint.highPercentage + (highSlope * i))),
          medPercentage: Math.max(0, Math.min(100, lastPoint.medPercentage + (medSlope * i))),
          lowPercentage: Math.max(0, Math.min(100, lastPoint.lowPercentage + (lowSlope * i))),
          isProjection: true
        });
      }
    }

    // Calculate market sentiment
    const latestPoint = processed[processed.length - 1];
    const sentiment = calculateSentiment(latestPoint);
    const momentum = calculateMomentum(processed);

    return {
      chartData: processed,
      extrapolatedData: extrapolated,
      stats: { sentiment, momentum }
    };
  }, [timeSeriesData]);

  const maxValue = 100;
  const allData = [...chartData, ...extrapolatedData];

  if (allData.length === 0) {
    return (
      <div className="confidence-trend-chart">
        <div className="no-data">No trend data available yet</div>
      </div>
    );
  }

  return (
    <div className="confidence-trend-chart">
      <div className="chart-header">
        <div className="chart-title">
          <h3>CONFIDENCE INDEX</h3>
          <div className="market-indicators">
            <span className={`sentiment sentiment-${stats.sentiment}`}>
              {stats.sentiment.toUpperCase()}
            </span>
            <span className={`momentum momentum-${stats.momentum}`}>
              {stats.momentum === 'increasing' ? '▲' : stats.momentum === 'decreasing' ? '▼' : '—'} 
              MOMENTUM: {stats.momentum.toUpperCase()}
            </span>
            <span className="volume-indicator">
              VOL: {chartData.reduce((sum, d) => sum + d.total, 0)}
            </span>
          </div>
        </div>
        
        <div className="legend">
          <div className="legend-item">
            <span className="legend-color high"></span>HIGH
          </div>
          <div className="legend-item">
            <span className="legend-color med"></span>MED
          </div>
          <div className="legend-item">
            <span className="legend-color low"></span>LOW
          </div>
          <div className="legend-item">
            <span className="legend-color projection"></span>Projection
          </div>
        </div>
      </div>

      <div className="chart-container">
        <svg viewBox={`0 0 1400 300`} className="trend-svg" preserveAspectRatio="xMinYMin meet">
          {/* Grid lines */}
          {[0, 25, 50, 75, 100].map(y => (
            <g key={y}>
              <line
                x1="50"
                y1={250 - (y * 2)}
                x2="1350"
                y2={250 - (y * 2)}
                stroke="#e5e7eb"
                strokeDasharray={y === 50 ? "0" : "2,2"}
              />
              <text
                x="35"
                y={255 - (y * 2)}
                fill="#6b7280"
                fontSize="12"
                textAnchor="end"
              >
                {y}%
              </text>
            </g>
          ))}

          {/* Projection zone */}
          {extrapolatedData.length > 0 && allData.length > 0 && (
            <rect
              x={50 + (chartData.length / Math.max(allData.length, 1)) * 700}
              y="50"
              width={(extrapolatedData.length / Math.max(allData.length, 1)) * 700}
              height="200"
              fill="url(#projectionGradient)"
              opacity="0.1"
            />
          )}

          {/* Data lines */}
          {allData.length > 0 && ['high', 'med', 'low'].map(level => {
            const points = allData.map((point, i) => {
              const x = 50 + (i / Math.max(allData.length - 1, 1)) * 1300;
              const y = 250 - ((point[`${level}Percentage`] || 0) * 2);
              return `${x},${y}`;
            }).join(' ');

            const realPoints = chartData.map((point, i) => {
              const x = 50 + (i / Math.max(allData.length - 1, 1)) * 1300;
              const y = 250 - ((point[`${level}Percentage`] || 0) * 2);
              return `${x},${y}`;
            }).join(' ');

            return (
              <g key={level}>
                <polyline
                  points={realPoints}
                  fill="none"
                  stroke={level === 'high' ? '#10b981' : level === 'med' ? '#f59e0b' : '#ef4444'}
                  strokeWidth="2"
                />
                {extrapolatedData.length > 0 && (
                  <polyline
                    points={points}
                    fill="none"
                    stroke={level === 'high' ? '#10b981' : level === 'med' ? '#f59e0b' : '#ef4444'}
                    strokeWidth="2"
                    strokeDasharray="5,5"
                    opacity="0.5"
                  />
                )}
              </g>
            );
          })}

          {/* Volume bars */}
          {chartData.length > 0 && chartData.map((point, i) => {
            const x = 50 + (i / Math.max(allData.length - 1, 1)) * 1300;
            const maxVolume = Math.max(...chartData.map(p => p.total || 0), 1);
            const barHeight = ((point.total || 0) / maxVolume) * 60;
            const barWidth = Math.max(700 / Math.max(allData.length, 1) - 2, 4);
            
            return (
              <rect
                key={`vol-${i}`}
                x={x - barWidth/2}
                y={350 - barHeight}
                width={barWidth}
                height={barHeight}
                fill="rgba(59, 130, 246, 0.3)"
                stroke="rgba(59, 130, 246, 0.5)"
                strokeWidth="1"
              />
            );
          })}

          {/* Data points */}
          {chartData.length > 0 && chartData.map((point, i) => {
            const x = 50 + (i / Math.max(allData.length - 1, 1)) * 1300;
            return (
              <g key={i}>
                <circle cx={x} cy={250 - ((point.highPercentage || 0) * 2)} r="3" fill="#10b981" />
                <circle cx={x} cy={250 - ((point.medPercentage || 0) * 2)} r="3" fill="#f59e0b" />
                <circle cx={x} cy={250 - ((point.lowPercentage || 0) * 2)} r="3" fill="#ef4444" />
              </g>
            );
          })}

          {/* Volume label */}
          <text x="25" y="340" fill="#6b7280" fontSize="10" textAnchor="middle">VOL</text>
          
          {/* Separator line between price and volume */}
          <line x1="50" y1="285" x2="1350" y2="285" stroke="#374151" strokeWidth="1" />

          {/* Time labels */}
          {allData.length > 0 && allData.filter((_, i) => i % Math.ceil(allData.length / 8) === 0).map((point, i) => {
            const x = 50 + (allData.indexOf(point) / Math.max(allData.length - 1, 1)) * 1300;
            const time = new Date(point.timestamp);
            return (
              <text
                key={i}
                x={x}
                y={380}
                fill="#6b7280"
                fontSize="11"
                textAnchor="middle"
              >
                {!isNaN(time.getHours()) ? `${time.getHours()}:00` : ''}
              </text>
            );
          })}

          <defs>
            <linearGradient id="projectionGradient" x1="0%" y1="0%" x2="100%" y2="0%">
              <stop offset="0%" stopColor="#667eea" />
              <stop offset="100%" stopColor="#764ba2" />
            </linearGradient>
          </defs>
        </svg>
      </div>

      <div className="trend-insights">
        <div className="insight-card">
          <span className="insight-label">Current Mix:</span>
          <span className="insight-value">
            {chartData.length > 0 && 
              `HIGH: ${chartData[chartData.length-1].highPercentage.toFixed(1)}% | 
               MED: ${chartData[chartData.length-1].medPercentage.toFixed(1)}% | 
               LOW: ${chartData[chartData.length-1].lowPercentage.toFixed(1)}%`
            }
          </span>
        </div>
        {extrapolatedData.length > 0 && (
          <div className="insight-card projection">
            <span className="insight-label">24hr Projection:</span>
            <span className="insight-value">
              {`HIGH: ${extrapolatedData[extrapolatedData.length-1].highPercentage.toFixed(1)}% | 
                MED: ${extrapolatedData[extrapolatedData.length-1].medPercentage.toFixed(1)}% | 
                LOW: ${extrapolatedData[extrapolatedData.length-1].lowPercentage.toFixed(1)}%`
              }
            </span>
          </div>
        )}
      </div>
    </div>
  );
};

export default ConfidenceTrendChart;